import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, MapPin, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { Logo } from "@/components/Logo";

const TOP_SERVICE = "window-screen-repair"; // default service every city page should link to from the hub

export default function HubLocations() {
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [s, c] = await Promise.all([
          api.get("/seo/states"),
          api.get("/seo/cities"),
        ]);
        setStates(s.data?.states || []);
        setCities(c.data?.cities || []);
      } catch (_e) {
        /* graceful — empty render */
      }
    })();
  }, []);

  const grouped = states.map((st) => ({
    ...st,
    cities: cities.filter((c) => c.state_slug === st.slug),
  }));

  return (
    <div className="min-h-screen bg-[#FBFAF6]">
      <Helmet>
        <title>Find Window Screen Pros Near You — WindowScreenQuote</title>
        <meta name="description" content="Browse window screen and sliding door screen pros across the US. Real local pricing, real local pros, instant quotes." />
        <link rel="canonical" href="https://windowscreenquote.com/locations" />
      </Helmet>

      <SiteNav />

      <main className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 py-14 lg:py-20" data-testid="locations-hub">
        <div className="text-xs font-bold uppercase tracking-widest text-[#C75D33] mb-3">Service Areas</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight">Find a screen pro near you</h1>
        <p className="text-slate-600 mt-4 text-lg max-w-2xl leading-relaxed">
          We&rsquo;re live in {grouped.length || "—"} states and {cities.length || "—"} cities — and growing. Tap your area to see local pricing for window screens, sliding doors, and more.
        </p>

        <div className="mt-10 flex flex-wrap gap-3">
          <Link to="/quote"><Button size="lg" className="bg-[#0F172A] hover:bg-black rounded-2xl h-12 px-6 text-sm font-bold" data-testid="locations-quote-cta">Get an instant quote <ArrowRight className="w-4 h-4 ml-1.5" /></Button></Link>
        </div>

        <div className="mt-12 space-y-8">
          {grouped.map((st) => (
            <section key={st.slug} className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-7">
              <div className="flex items-center gap-2 mb-4">
                <MapPin className="w-4 h-4 text-[#C75D33]" />
                <Link to={`/${st.slug}/${TOP_SERVICE}`} className="font-display font-bold text-xl text-slate-900 hover:text-[#C75D33]" data-testid={`hub-state-${st.slug}`}>
                  {st.name}
                </Link>
                <span className="text-xs font-mono text-slate-400">{st.code}</span>
              </div>
              {st.cities.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-x-4 gap-y-2 text-sm">
                  {st.cities.map((c) => (
                    <Link
                      key={c.slug}
                      to={`/${st.slug}/${c.slug}/${TOP_SERVICE}`}
                      className="text-slate-700 hover:text-[#C75D33] hover:underline"
                      data-testid={`hub-city-${c.slug}`}
                    >
                      {c.name}
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-slate-500">Coming soon.</div>
              )}
            </section>
          ))}
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}

function SiteNav() {
  return (
    <nav className="px-5 sm:px-8 lg:px-12 py-4 flex items-center justify-between border-b border-slate-200/60 bg-white/80 backdrop-blur sticky top-0 z-40">
      <Link to="/" className="flex items-center gap-2.5">
        <Logo size={36} withWordmark />
      </Link>
      <Link to="/" className="text-sm font-semibold text-slate-600 hover:text-slate-900">Home</Link>
    </nav>
  );
}

function SiteFooter() {
  return (
    <footer className="border-t border-slate-200 py-8 px-5 sm:px-8 lg:px-12 bg-white">
      <div className="max-w-6xl mx-auto flex flex-col sm:flex-row justify-between gap-3 text-sm text-slate-500">
        <span>© WindowScreenQuote.com {new Date().getFullYear()}</span>
        <div className="flex flex-wrap gap-5">
          <Link to="/" className="hover:text-slate-900">Home</Link>
          <Link to="/services" className="hover:text-slate-900">Services</Link>
          <Link to="/terms" className="hover:text-slate-900">Terms</Link>
          <Link to="/privacy" className="hover:text-slate-900">Privacy</Link>
        </div>
      </div>
    </footer>
  );
}
